import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { founders, partnerCategories, principles } from "@/data/content";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function ChiSiamo() {
  useSeo({
    title: "Chi Siamo — boutique immobiliare etica",
    description:
      "La storia, la missione e i soci fondatori di Opera Real Estate Partners. Una rete di partner professionali al servizio di investimenti immobiliari etici e redditizi.",
  });

  return (
    <div>
      <section className="bg-[#0D1B2A] pt-20 pb-16 relative noise-overlay border-b border-white/5">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 relative z-10">
          <span className="label-eyebrow">Chi Siamo</span>
          <Reveal>
            <h1 className="font-display font-light text-4xl lg:text-6xl text-white mt-4 max-w-3xl leading-tight">
              Una boutique che mette il metodo prima del mattone
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-gray-300 text-lg mt-8 max-w-3xl leading-relaxed">
              Opera Real Estate Partners nasce dalla convinzione che il real estate ad alta
              redditività e l'etica non siano in contraddizione, ma la stessa cosa fatta bene.
              Operiamo su immobili complessi — aste, posizioni debitorie, sviluppo, reddito — con
              disciplina quantitativa e un protocollo di tutela delle persone coinvolte.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-[#1E2229] py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-12">
          <Reveal>
            <span className="label-eyebrow">Missione</span>
            <p className="font-serif-claim italic text-2xl lg:text-3xl text-white mt-4 leading-snug">
              Generare rendimento prevedibile trasformando la complessità immobiliare in valore,
              senza mai speculare sulla fragilità di chi vende.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="label-eyebrow">Come lo facciamo</span>
            <ul className="mt-4 space-y-4">
              {principles.map((p) => (
                <li key={p.n} className="flex gap-4">
                  <span className="font-serif-claim text-2xl text-[#C5A059]">{p.n}</span>
                  <span className="text-gray-300 leading-relaxed">{p.title}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="bg-[#0D1B2A] py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <span className="label-eyebrow">I soci fondatori</span>
          <h2 className="font-display font-light text-3xl lg:text-4xl text-white mt-4">
            Responsabilità condivisa, competenze distinte
          </h2>
          <div className="grid md:grid-cols-2 gap-8 mt-14">
            {founders.map((f, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-[#1E2229] border border-white/5 h-full">
                  <div className="aspect-[3/2] overflow-hidden bg-[#091623] flex items-center justify-center relative noise-overlay">
                    <div className="relative z-10 h-28 w-28 rounded-full border-2 border-[#C5A059]/60 flex items-center justify-center bg-[#0D1B2A]">
                      <span className="font-display font-extrabold text-4xl text-[#C5A059] tracking-wider">{f.initials}</span>
                    </div>
                  </div>
                  <div className="p-8">
                    <div className="label-eyebrow">{f.role}</div>
                    <h3 className="font-display text-2xl text-white mt-2">{f.name}</h3>
                    <p className="text-sm text-[#C5A059] mt-2">{f.area}</p>
                    <p className="text-gray-400 mt-4 leading-relaxed">{f.bio}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER NETWORK */}
      <section className="bg-[#1E2229] py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="label-eyebrow">Rete di partner</span>
            <h2 className="font-display font-light text-3xl lg:text-4xl text-white mt-4 max-w-2xl">
              Un ecosistema di professionisti selezionati
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl leading-relaxed">
              Ogni operazione è supportata da una rete consolidata di professionisti. Collaboriamo
              stabilmente con figure specializzate, citate qui come categorie.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px mt-12 bg-white/5">
            {partnerCategories.map((c, i) => (
              <Reveal key={c} delay={(i % 4) * 0.05}>
                <div className="bg-[#1E2229] p-6 h-full text-gray-200 font-display">{c}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0D1B2A] py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Link
            to="/contatti"
            className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0D1B2A] hover:bg-[#D8B978] transition-colors font-medium px-8 py-4"
          >
            Lavora con noi <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
